import { useRouterState } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModeToggle } from '@/components/mode-toggle';
import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user';
import { ProjectSwitcher } from '@/components/project-switcher';
import { Separator } from '@/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { useUser } from '@/hooks/use-user';
import { navigation } from '@/navigation';
import { sidebarProjectsConfig } from '@/sidebar-config';

type RouteStaticData = {
  titleKey?: string;
  title?: string;
};

export function AppLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation('navigation');
  const { location, matches } = useRouterState({
    select: (state) => ({
      location: state.location,
      matches: state.matches,
    }),
  });
  const { user } = useUser();

  const pathname = location.pathname;

  const activeMatch = matches.at(-1);
  const activeStaticData = (activeMatch?.staticData ?? {}) as RouteStaticData;
  const pageTitle =
    (activeStaticData.titleKey
      ? t(activeStaticData.titleKey)
      : activeStaticData.title) ?? 'Application';

  useEffect(() => {
    document.title = `${pageTitle} - POM`;
  }, [pageTitle]);

  return (
    <SidebarProvider>
      <Sidebar className='bg-sidebar' collapsible='icon'>
        <SidebarHeader className='bg-sidebar'>
          <ProjectSwitcher projects={sidebarProjectsConfig} />
        </SidebarHeader>
        <SidebarContent className='bg-sidebar'>
          {navigation.map((section) => (
            <NavMain
              items={section.items.map((item) => {
                const isActive =
                  item.href === '/'
                    ? pathname === '/'
                    : pathname === item.href ||
                      pathname.startsWith(`${item.href}/`);

                return {
                  title: item.labelKey,
                  url: item.href,
                  icon: item.icon,
                  isActive,
                };
              })}
              key={section.id}
              sectionLabel={section.labelKey || undefined}
            />
          ))}
        </SidebarContent>
        <SidebarFooter className='bg-sidebar'>
          <NavUser
            email={user?.email || ''}
            firstname={user?.firstName || ''}
            lastname={user?.lastName || ''}
          />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset className='max-h-svh overflow-hidden'>
        <header className='flex h-14 shrink-0 items-center gap-2 border-b bg-background/80 px-4 backdrop-blur'>
          <div className='flex flex-1 items-center gap-2'>
            <SidebarTrigger />
            <Separator className='h-6' orientation='vertical' />
            <div className='flex'>
              <span className='font-semibold text-muted-foreground text-sm leading-none'>
                {pageTitle}
              </span>
            </div>
          </div>
          <ModeToggle />
        </header>
        <main className='min-h-0 flex-1 overflow-auto bg-muted/30 px-4 py-6'>
          <div className='mx-auto flex h-full max-w-6xl flex-col'>
            {children}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
